/**
 * The Request access modal's signing panel: shown over the basket once a
 * submit made drafts, it runs the passkey prompt for each draft in turn (see
 * draftSigning.ts).
 *
 * A large badge is the focal point, with the status under it ("Sign this
 * request", "Waiting for your passkey…", "Sent", or what went wrong) and,
 * for several drafts, how far along it is. The button the browser may need
 * (or Try again) sits right under it, and wherever a prompt did not go
 * through, a quiet "No passkey on this device? Add one here" under that: the
 * browser cannot tell a closed prompt from a device with no passkey for the
 * account. What is being signed is one click away, under "See details",
 * collapsed at first.
 *
 * No code: the portal made these requests, so there is no other screen to
 * match one against. The signing page keeps the code for links from the CLI,
 * the extension and the desktop app.
 *
 * `DraftSigningView` is the markup, one function of the state, so each state
 * renders in a test without a browser. The default export adds what needs
 * React: the account name and where focus goes.
 */
import {
  Box,
  Collapse,
  Flex,
  Heading,
  Icon,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useEffect, useId, useRef, type Ref } from "react";
import type { IconType } from "react-icons";
import {
  TbAlertTriangle,
  TbCheck,
  TbChevronDown,
  TbClock,
  TbFingerprint,
} from "react-icons/tb";

import PloyButton from "components/ds/PloyButton";

import type { StepUpChallenge } from "state/members/stepUp";

import { canAddHere, type DraftSigningState } from "./draftSigning";
import type { KnownAccount } from "./payloadProse";
import {
  EXPIRED_LINE,
  PayloadSummary,
  SigningBadge,
  USED_LINE,
  type Tone,
} from "./signingParts";
import type { DraftSigning } from "./useDraftSigning";
import { useKnownAccount } from "./useKnownAccount";

type Shown = Exclude<DraftSigningState, { name: "idle" }>;

/** What the panel asks; its accessible name, and the mobile sheet's. */
export const DRAFT_SIGNING_TITLE = "Sign with your passkey to send this request";

const CANCEL_HINT = "If you cancel, it waits in My Access, unsigned.";

type Look = {
  tone: Tone;
  icon: IconType;
  /** The status under the badge. */
  title: string;
  line: string;
  /** The button under the badge, or none. `sign` opens the prompt, `retry` starts over. */
  action: { label: string; does: "sign" | "retry" } | null;
};

const look = (state: Shown): Look => {
  const passkey = { tone: "p" as const, icon: TbFingerprint };
  const retry = { label: "Try again", does: "retry" as const };
  switch (state.name) {
    case "preparing":
      return {
        ...passkey,
        title: "Sign this request",
        line: "Getting it ready for your passkey…",
        action: null,
      };
    case "signing":
      return {
        ...passkey,
        title: "Waiting for your passkey…",
        line: "Follow your browser's prompt to sign and send it.",
        action: null,
      };
    case "ready":
      return state.reason === "gesture"
        ? {
            ...passkey,
            title: "Sign this request",
            line: "Use your passkey to send it for approval.",
            action: { label: "Sign with passkey", does: "sign" },
          }
        : {
            ...passkey,
            title: "Not signed yet",
            line: "The passkey prompt closed without signing. Nothing was sent.",
            action: { label: "Try again", does: "sign" },
          };
    case "sent":
      return {
        tone: "success",
        icon: TbCheck,
        title: "Sent",
        line:
          state.signed === 1
            ? "Your request is signed and sent for approval."
            : `All ${state.signed} requests are signed and sent for approval.`,
        action: null,
      };
    case "problem":
      switch (state.problem) {
        case "expired":
          return {
            tone: "warning",
            icon: TbClock,
            title: "Signing timed out",
            line: EXPIRED_LINE,
            action: retry,
          };
        case "used":
          return {
            tone: "warning",
            icon: TbAlertTriangle,
            title: "Already used",
            line: `${state.message} ${USED_LINE}`,
            action: retry,
          };
        case "unsupported":
          return {
            tone: "warning",
            icon: TbAlertTriangle,
            title: "Passkeys are not available here",
            line: "This browser cannot use passkeys. Your request waits in My Access: sign it there in a recent version of Chrome, Safari, Edge or Firefox.",
            action: null,
          };
        default:
          return {
            tone: "error",
            icon: TbAlertTriangle,
            title: "Signing did not work",
            line: `${state.message} Nothing was signed.`,
            action: retry,
          };
      }
  }
};

/**
 * What is being signed, behind "See details": collapsed at first, so the
 * badge stays the focal point. A plain disclosure (a button that says whether
 * it is expanded and names the region it opens) rather than an accordion of
 * one, which would only know its items after the first paint.
 */
const Details = ({
  challenge,
  account,
  defaultOpen,
}: {
  challenge: StepUpChallenge;
  account?: KnownAccount | null;
  defaultOpen: boolean;
}) => {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: defaultOpen });
  const regionId = useId();
  return (
    <Box w="full">
      <PloyButton
        data-testid="draft-signing-details-toggle"
        variant="ghost"
        size="sm"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={regionId}
        color="p.600"
        _hover={{ color: "p.700", bg: "p.50" }}
        rightIcon={
          <Icon
            as={TbChevronDown}
            boxSize="14px"
            aria-hidden
            transform={isOpen ? "rotate(180deg)" : "none"}
            transition="transform 160ms cubic-bezier(0.23, 1, 0.32, 1)"
            sx={{ "@media (prefers-reduced-motion: reduce)": { transition: "none" } }}
          />
        }
      >
        See details
      </PloyButton>
      <Box id={regionId} data-testid="draft-signing-details" textAlign="left">
        <Collapse in={isOpen} animateOpacity unmountOnExit>
          <Box pt="sm">
            <PayloadSummary challenge={challenge} account={account} compact />
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

export type DraftSigningViewProps = {
  state: Shown;
  account?: KnownAccount | null;
  onSign: () => void;
  onRetry: () => void;
  onCancel: () => void;
  /** "Add one here": the signing page adds a passkey on this device, then signs. */
  onAddHere: () => void;
  /** Open the details from the start (they start collapsed). */
  defaultDetailsOpen?: boolean;
  headingRef?: Ref<HTMLHeadingElement>;
  actionRef?: Ref<HTMLButtonElement>;
};

export const DraftSigningView = ({
  state,
  account,
  onSign,
  onRetry,
  onCancel,
  onAddHere,
  defaultDetailsOpen = false,
  headingRef,
  actionRef,
}: DraftSigningViewProps) => {
  const shown = look(state);
  const challenge = "challenge" in state ? state.challenge : null;
  const { index, total } = state.step;
  const sent = state.name === "sent";
  const stepLabel = total > 1 && !sent ? `Signing ${index + 1} of ${total}` : null;
  const unsupported = state.name === "problem" && state.problem === "unsupported";

  return (
    <Stack
      role="region"
      aria-label={DRAFT_SIGNING_TITLE}
      spacing="1xl"
      align="center"
      textAlign="center"
      w="full"
    >
      <Stack role="status" aria-live="polite" spacing="md" align="center">
        <SigningBadge
          testId="draft-signing-badge"
          tone={shown.tone}
          icon={shown.icon}
          waiting={state.name === "signing"}
        />
        {stepLabel && (
          <Box
            fontSize="xs"
            fontWeight={500}
            color="neutral.700"
            bg="neutral.100"
            borderRadius="full"
            px="sm"
            py="2px"
            sx={{ fontVariantNumeric: "tabular-nums" }}
          >
            {stepLabel}
          </Box>
        )}
        <Stack spacing="2xs" align="center" maxW="340px">
          <Heading
            as="h2"
            ref={headingRef}
            tabIndex={-1}
            fontSize="xl"
            fontWeight={600}
            color="neutral.950"
            lineHeight="1.25"
            _focus={{ outline: "none" }}
          >
            {shown.title}
          </Heading>
          <Text fontSize="smd" color="neutral.700">
            {shown.line}
          </Text>
        </Stack>
      </Stack>

      {shown.action && (
        <Stack spacing="sm" align="center">
          <PloyButton
            ref={actionRef}
            variant="primary"
            size="md"
            onClick={shown.action.does === "sign" ? onSign : onRetry}
            leftIcon={<TbFingerprint size={16} aria-hidden />}
          >
            {shown.action.label}
          </PloyButton>
          {canAddHere(state) && (
            <Text fontSize="smd" color="neutral.600">
              No passkey on this device?{" "}
              <PloyButton
                variant="azurite-link"
                onClick={onAddHere}
                fontSize="inherit"
                lineHeight="inherit"
                verticalAlign="baseline"
              >
                Add one here
              </PloyButton>
            </Text>
          )}
        </Stack>
      )}

      {challenge && (
        <Details
          // A new draft starts collapsed again.
          key={challenge.id}
          challenge={challenge}
          account={account}
          defaultOpen={defaultDetailsOpen}
        />
      )}

      {!sent && (
        <Stack spacing="2xs" align="center">
          <PloyButton variant="ghost" size="sm" onClick={onCancel}>
            {unsupported ? "Close" : "Cancel"}
          </PloyButton>
          {!unsupported && (
            <Text fontSize="xs" color="neutral.600">
              {CANCEL_HINT}
            </Text>
          )}
        </Stack>
      )}
    </Stack>
  );
};

/**
 * The panel for a signing run. Focus lands on the status when the panel
 * opens, and on the button when the next step needs a click, so a keyboard
 * or screen reader is always where the panel is.
 */
const DraftSigningPanel = ({ signing }: { signing: DraftSigning }) => {
  const { state } = signing;
  const challenge = "challenge" in state ? state.challenge : null;
  const account = useKnownAccount(challenge ?? undefined, null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const actionRef = useRef<HTMLButtonElement>(null);
  const waiting = state.name === "ready" || state.name === "problem";

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  useEffect(() => {
    if (waiting) (actionRef.current ?? headingRef.current)?.focus();
  }, [waiting, state]);

  if (state.name === "idle") return null;
  return (
    <DraftSigningView
      state={state}
      account={account}
      onSign={signing.sign}
      onRetry={signing.retry}
      onCancel={signing.stop}
      onAddHere={signing.addHere}
      headingRef={headingRef}
      actionRef={actionRef}
    />
  );
};

export default DraftSigningPanel;
